<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>القاعات</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          إضافة قاعة
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="rooms"
      :search="search"
      class="elevation-1"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-center">{{ item.name }}</td>
          <td class="text-center">{{ item.capacity }}</td>
          <td class="text-center">{{ item.price_per_hour }}</td>
          <td class="text-center">{{ item.price_per_day }}</td>
          <td class="text-center">
            <v-icon :color="item.has_wifi ? 'green' : 'grey'">mdi-wifi</v-icon>
            <v-icon :color="item.has_projector ? 'green' : 'grey'" class="mx-2">mdi-projector</v-icon>
            <v-icon :color="item.has_tv ? 'green' : 'grey'">mdi-television</v-icon>
          </td>
          <td class="text-center">{{ item.tv_size || '-' }}</td>
          <td class="text-center">{{ item.note }}</td>
          <td class="text-center">
            <v-icon
              :color="item.active ? 'green' : 'red'"
              @click="toggleRoomStatus(item)"
            >
              mdi-power
            </v-icon>
          </td>
          <td class="text-center">
            <v-icon small class="mr-2" @click="editRoom(item)" title="تعديل">mdi-pencil</v-icon>
            <v-icon small @click="showDeleteConfirm(item.id)" title="حذف">mdi-delete</v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Add Room Dialog -->
    <v-dialog v-model="showAddDialog" max-width="600px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">إضافة قاعة جديدة</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="newRoom.name"
                label="اسم القاعة"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="newRoom.capacity"
                label="السعة"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="newRoom.price_per_hour"
                label="السعر بالساعة"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="newRoom.price_per_day"
                label="السعر باليوم"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="newRoom.has_wifi"
                label="واي فاي"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="newRoom.has_projector"
                label="بروجيكتور"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="newRoom.has_tv"
                label="تلفاز"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="newRoom.tv_size"
                label="حجم التلفاز"
                type="number"
                class="rtl-input"
                :disabled="!newRoom.has_tv"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="newRoom.note"
                label="ملاحظات"
                class="rtl-input"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showAddDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="addNewRoom">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Room Dialog -->
    <v-dialog v-model="showEditDialog" max-width="600px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">تعديل القاعة</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="currentRoom.name"
                label="اسم القاعة"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="currentRoom.capacity"
                label="السعة"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="currentRoom.price_per_hour"
                label="السعر بالساعة"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="currentRoom.price_per_day"
                label="السعر باليوم"
                type="number"
                class="rtl-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="currentRoom.has_wifi"
                label="واي فاي"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="currentRoom.has_projector"
                label="بروجيكتور"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="currentRoom.has_tv"
                label="تلفاز"
                class="rtl-input"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="currentRoom.tv_size"
                label="حجم التلفاز"
                type="number"
                class="rtl-input"
                :disabled="!currentRoom.has_tv"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="currentRoom.note"
                label="ملاحظات"
                class="rtl-input"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showEditDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="updateRoomDetails">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add confirmation dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400px">
      <v-card class="rtl-dialog">
        <v-card-title class="dialog-header">
          <span>تأكيد الحذف</span>
        </v-card-title>
        <v-card-text class="dialog-content">
          هل أنت متأكد من حذف هذه القاعة؟
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="confirmDeleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" variant="text" @click="confirmDelete">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'RoomsComponent',
  data() {
    return {
      search: '',
      isLoading: false,
      newRoom: {
        name: '',
        capacity: 0,
        price_per_hour: 0,
        price_per_day: 0,
        has_wifi: false,
        has_projector: false,
        has_tv: false,
        tv_size: null,
        active: true,
        note: ''
      },
      currentRoom: {},
      showAddDialog: false,
      showEditDialog: false,
      confirmDeleteDialog: false,
      roomToDelete: null,
      headers: [
        { title: 'اسم القاعة', key: 'name', align: 'center' },
        { title: 'السعة', key: 'capacity', align: 'center' },
        { title: 'السعر بالساعة', key: 'price_per_hour', align: 'center' },
        { title: 'السعر باليوم', key: 'price_per_day', align: 'center' },
        { title: 'المميزات', key: 'features', align: 'center' },
        { title: 'حجم التلفاز', key: 'tv_size', align: 'center' },
        { title: 'ملاحظات', key: 'note', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ]
    };
  },

  computed: {
    ...mapGetters(['allRooms']),
    rooms() {
      return this.allRooms;
    }
  },

  methods: {
    ...mapActions(['fetchRooms', 'addRoom', 'updateRoom', 'deleteRoom']),
    
    editRoom(item) {
      this.currentRoom = { ...item };
      this.showEditDialog = true;
    },

    async toggleRoomStatus(item) {
      try {
        const roomData = { ...item };
        roomData.active = !roomData.active;
        await this.updateRoom(roomData);
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث حالة القاعة:', error);
      }
    },

    async addNewRoom() {
      try {
        await this.addRoom(this.newRoom);
        this.showAddDialog = false;
        await this.refreshTable();
        
        this.newRoom = {
          name: '',
          capacity: 0,
          price_per_hour: 0,
          price_per_day: 0,
          has_wifi: false,
          has_projector: false,
          has_tv: false,
          tv_size: null,
          active: true,
          note: ''
        };
      } catch (error) {
        console.error('خطأ في إضافة القاعة:', error);
      }
    },

    async updateRoomDetails() {
      try {
        await this.updateRoom(this.currentRoom);
        this.showEditDialog = false;
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث بيانات القاعة:', error);
      }
    },

    handleSearch: debounce(async function() {
      console.log('Search triggered with:', this.search);
      
      try {
        this.isLoading = true;
        if (!this.search || !this.search.trim()) {
          console.log('Empty search - fetching all rooms');
          await this.fetchRooms();
        } else {
          const searchTerm = this.search.trim();
          console.log('Searching for:', searchTerm);
          await this.fetchRooms({ name: searchTerm });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        this.isLoading = false;
      }
    }, 300),

    async refreshTable() {
      console.log('Refreshing table with search:', this.search);
      
      try {
        this.isLoading = true;
        if (this.search && this.search.trim()) {
          await this.fetchRooms({ name: this.search.trim() });
        } else {
          await this.fetchRooms();
        }
      } catch (error) {
        console.error('خطأ في تحديث الجدول:', error);
      } finally {
        this.isLoading = false;
      }
    },

    showDeleteConfirm(id) {
      this.roomToDelete = id;
      this.confirmDeleteDialog = true;
    },

    confirmDelete() {
      this.deleteRoom(this.roomToDelete)
        .then(() => {
          this.$store.dispatch('showSnackbar', {
            message: 'تم حذف القاعة بنجاح',
            color: 'success'
          });
        })
        .catch(error => {
          console.log(error);
          this.$store.dispatch('showSnackbar', {
            message: 'حدث خطأ أثناء حذف القاعة',
            color: 'error'
          });
        })
        .finally(() => {
          this.confirmDeleteDialog = false;
          this.roomToDelete = null;
        });
    }
  },

  async created() {
    try {
      await this.refreshTable();
    } catch (error) {
      console.error('خطأ في تهيئة الصفحة:', error);
    }
  },

  watch: {
    search: {
      handler(newVal) {
        console.log('Search query changed:', newVal);
        this.handleSearch();
      }
    },
    showAddDialog(val) {
      if (!val) {
        this.$nextTick(() => {
          this.currentRoom = {
            name: '',
            capacity: 0,
            price_per_hour: 0,
            price_per_day: 0,
            has_wifi: false,
            has_projector: false,
            has_tv: false,
            tv_size: null,
            active: true,
            note: ''
          };
          this.showEditDialog = false;
        });
      }
    }
  }
};
</script>

<!-- Rooms styles are now handled globally in src/assets/styles/global.css -->
